// Push notifications inside the Twinr Android app.
//
// The app loads this website in a webview and adds a JavaScript "bridge":
// `window.twinr_push_token_fetch()` gives the phone's push token. We send that token to our
// server for the signed-in user, so the server can later push to them, and remove it just
// before logout. On a normal browser none of this runs.
//
// If the phone cannot give a token, we save the full error on the server instead, because
// we cannot watch the app's live logs. All of it is silent: the user never sees an error.
//
// See https://help.twinr.dev/js-bridge/push-notification-device-token

/* global twinr_push_token_fetch, twinr_device_info */

import { postPushRegister, postPushUnregister } from './api';

// The bridge can be added a little after the page starts. We look once more after this.
const BRIDGE_WAIT_MS = 2000;
// If the token call still fails because the function is missing, we wait until the page has
// fully loaded, then this long, and try one last time.
const BRIDGE_RETRY_AFTER_LOAD_MS = 5000;
// We never wait longer than this for the page `load` event.
const PAGE_LOAD_TIMEOUT_MS = 30000;
// Logout waits for the unregister request at most this long.
const UNREGISTER_TIMEOUT_MS = 2000;
// How long we wait for the optional device info (only used for the app version).
const DEVICE_INFO_TIMEOUT_MS = 1000;
// Keeps the request well under the server's 100 KB body limit. The server cuts at 50 KB too.
const MAX_ERROR_LENGTH = 50 * 1024;
const TRUNCATED_MARK = '\n[truncated]';
const PLATFORMS = ['android', 'ios'];

// The user whose token this page load already sent (or is sending). `fetchCurrentUser`
// runs many times per page, but we register once per signed-in user.
let registeredUserId = null;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

// Resolves with `fallback` if the promise is too slow. Never rejects.
const withTimeout = (promise, ms, fallback) => {
  let timer;
  const timeout = new Promise(resolve => {
    timer = setTimeout(() => resolve(fallback), ms);
  });
  return Promise.race([Promise.resolve(promise).catch(() => fallback), timeout]).finally(() =>
    clearTimeout(timer)
  );
};

// The app can add its functions in two ways. On `window`, or as a bare global (for example
// `const twinr_push_token_fetch = ...`), which is NOT on `window`. Twinr support asked us to
// try both: `window.twinr_x()` and `twinr_x()`. `typeof` on a missing bare name is safe.

// Calls the push token function the way it exists. If it does not exist at all, calls
// `window.twinr_push_token_fetch()` so the thrown error says exactly that.
const callPushTokenFetch = () =>
  typeof window.twinr_push_token_fetch !== 'function' &&
  typeof twinr_push_token_fetch === 'function'
    ? twinr_push_token_fetch()
    : window.twinr_push_token_fetch();

// Same for the device info function.
const callDeviceInfo = () =>
  typeof window.twinr_device_info !== 'function' && typeof twinr_device_info === 'function'
    ? twinr_device_info()
    : window.twinr_device_info();

const hasPushTokenFetch = () =>
  typeof window.twinr_push_token_fetch === 'function' ||
  typeof twinr_push_token_fetch === 'function';

const hasDeviceInfo = () =>
  typeof window.twinr_device_info === 'function' || typeof twinr_device_info === 'function';

/** Is the page running inside the Twinr app (with the push bridge)? Safe on the server. */
export const isTwinrApp = () => typeof window !== 'undefined' && hasPushTokenFetch();

/**
 * Is the page running inside an Android webview? Android adds `; wv)` to the user agent.
 *
 * Note: this is not Twinr only. Other apps' in-app browsers (Gmail, Facebook, …) match too.
 * If the Twinr Custom User Agent add-on is turned on later, check for its string here first.
 */
export const isAndroidWebview = () =>
  typeof navigator !== 'undefined' && /; wv\)/.test(navigator.userAgent || '');

/** Any thrown value as text. Keeps `message` and `stack` of an Error. Never throws. */
export const serializeError = e => {
  try {
    const text =
      e instanceof Error ? JSON.stringify(e, Object.getOwnPropertyNames(e)) : JSON.stringify(e);
    return text === undefined ? String(e) : text;
  } catch (stringifyError) {
    // For example a circular object.
    return String(e);
  }
};

const capError = text =>
  text.length > MAX_ERROR_LENGTH ? text.slice(0, MAX_ERROR_LENGTH) + TRUNCATED_MARK : text;

const toPlatform = (platform, fallback = 'unknown') =>
  PLATFORMS.includes(platform) ? platform : fallback;

const getAppVersion = async () => {
  if (typeof window === 'undefined' || !hasDeviceInfo()) {
    return undefined;
  }
  try {
    const info = await withTimeout(callDeviceInfo(), DEVICE_INFO_TIMEOUT_MS, null);
    return info?.appVersion;
  } catch (e) {
    return undefined;
  }
};

/**
 * Calls `twinr_device_info()` directly, with no `typeof` check, as Twinr support asked.
 * When the bridge is missing, this gives us the exact error the call throws. Never throws.
 *
 * Returns { ok: true, info } (without `pushToken`), or { ok: false, error }.
 */
export const probeDeviceInfo = async () => {
  const timedOut = { error: 'timeout' };
  try {
    const outcome = await withTimeout(
      Promise.resolve(callDeviceInfo()).then(
        info => ({ info }),
        e => ({ error: serializeError(e) })
      ),
      DEVICE_INFO_TIMEOUT_MS,
      timedOut
    );
    if ('error' in outcome) {
      return { ok: false, error: outcome.error };
    }
    // We never save tokens, so the push token in the device info is removed.
    const { pushToken, ...info } =
      outcome.info && typeof outcome.info === 'object' ? outcome.info : { value: outcome.info };
    return { ok: true, info };
  } catch (e) {
    return { ok: false, error: serializeError(e) };
  }
};

// Runs one way of calling a bridge function. Gives 'ok', or the exact error it threw.
// Only used when the function looks missing, so no token can end up in a report.
const tryCall = async call => {
  try {
    await call();
    return 'ok';
  } catch (e) {
    return serializeError(e);
  }
};

// Both ways of calling each bridge function, with the exact result of each.
const callBothWays = async () => ({
  pushTokenFetch: {
    windowType: typeof window.twinr_push_token_fetch,
    bareType: typeof twinr_push_token_fetch,
    windowCall: await tryCall(() => window.twinr_push_token_fetch()),
    bareCall: await tryCall(() => twinr_push_token_fetch()),
  },
  deviceInfo: {
    windowType: typeof window.twinr_device_info,
    bareType: typeof twinr_device_info,
    windowCall: await tryCall(() => window.twinr_device_info()),
    bareCall: await tryCall(() => twinr_device_info()),
  },
});

// Names of all `twinr*` things on `window`, to see if any part of the bridge was added.
const getTwinrGlobals = () => {
  try {
    return Object.keys(window).filter(key => /twinr/i.test(key));
  } catch (e) {
    return [];
  }
};

/**
 * Asks the bridge for the push token. Never throws.
 *
 * Returns { token, platform }, or { error, step, platform } when the bridge throws or gives
 * no token. The `error` never holds a token.
 */
export const getTwinrPushToken = async () => {
  try {
    const result = await callPushTokenFetch();
    const token = result?.token;
    const platform = toPlatform(result?.platform, 'android');
    if (typeof token === 'string' && token.length > 0) {
      return { token, platform };
    }
    // Probably what happens when notification permission is denied.
    const { token: removedToken, ...rest } =
      result && typeof result === 'object' ? result : { result };
    return { error: serializeError(rest), step: 'empty-token', platform };
  } catch (e) {
    return { error: serializeError(e), step: 'fetch', platform: 'android' };
  }
};

// One JSON string with everything we need to understand a failure later.
const buildErrorReport = async ({ step, platform, error, message }) => {
  const report = {
    timestamp: new Date().toISOString(),
    platform,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    bridgeExists: isTwinrApp(),
    step,
    ...(message ? { message } : {}),
    ...(error !== undefined ? { error } : {}),
  };
  const appVersion = await getAppVersion();
  if (appVersion) {
    report.appVersion = appVersion;
  }
  return capError(serializeError(report));
};

const reportError = async details => {
  const errorText = await buildErrorReport(details);
  return postPushRegister({ error: errorText, platform: details.platform });
};

// Sends the token or the error. If that request fails, reports it once, and stops there.
const sendBridgeResult = async result => {
  try {
    if (result.token) {
      await postPushRegister({ token: result.token, platform: result.platform });
    } else {
      await reportError(result);
    }
  } catch (e) {
    await reportError({
      step: 'register-request',
      platform: result.platform,
      error: serializeError(e),
    }).catch(() => {});
  }
};

const registerFromBridge = async () => sendBridgeResult(await getTwinrPushToken());

// Resolves when the page has fully loaded (at most after `PAGE_LOAD_TIMEOUT_MS`).
const waitForPageLoad = () => {
  if (document.readyState === 'complete') {
    return Promise.resolve();
  }
  const loaded = new Promise(resolve => window.addEventListener('load', resolve, { once: true }));
  return withTimeout(loaded, PAGE_LOAD_TIMEOUT_MS, undefined);
};

// The token call failed because the push function is not on the page.
const isBridgeMissing = result => !result.token && !isTwinrApp();

/**
 * The push function was still missing after the first wait. We call it directly anyway, so we
 * get the exact error it throws. If the function is still missing, we try one last time
 * after the page has fully loaded. If that fails too, we save one `bridge-missing` error with
 * the exact error of each try.
 */
const registerInWebviewWithoutBridge = async userId => {
  const firstTry = await getTwinrPushToken();
  let result = firstTry;
  if (isBridgeMissing(firstTry)) {
    await waitForPageLoad();
    await wait(BRIDGE_RETRY_AFTER_LOAD_MS);
    // The user logged out (or another user signed in) while we waited.
    if (registeredUserId !== userId) {
      return;
    }
    result = await getTwinrPushToken();
    if (isBridgeMissing(result)) {
      await reportError({
        step: 'bridge-missing',
        platform: 'android',
        message: 'bridge missing in webview',
        error: serializeError({
          pushTokenFetchErrors: [firstTry.error, result.error],
          bothWays: await callBothWays(),
          deviceInfoProbe: await probeDeviceInfo(),
          href: window.location.href,
          readyState: document.readyState,
          twinrGlobals: getTwinrGlobals(),
        }),
      });
      return;
    }
  }
  await sendBridgeResult(result);
};

/**
 * Sends this phone's push token for the signed-in user. Called after the current user is
 * fetched. Never throws, and does nothing on a normal browser.
 *
 * In an Android webview without the bridge, it waits ~2 seconds and looks once more. If the
 * bridge is still missing, it calls `twinr_push_token_fetch()` directly anyway, and tries once
 * more after the page has loaded. See `registerInWebviewWithoutBridge`.
 *
 * Runs at most once per user per page load.
 */
export const registerTwinrPushToken = async userId => {
  try {
    if (!userId || registeredUserId === userId) {
      return;
    }
    if (!isTwinrApp()) {
      if (!isAndroidWebview()) {
        return;
      }
      registeredUserId = userId;
      await wait(BRIDGE_WAIT_MS);
      if (!isTwinrApp()) {
        await registerInWebviewWithoutBridge(userId);
        return;
      }
    }
    registeredUserId = userId;
    await registerFromBridge();
  } catch (e) {
    // Silent on purpose: push registration must never affect the page.
  }
};

/**
 * Removes this phone's push token before logout, so it stops getting this user's
 * notifications. Must run before `sdk.logout()`, while the session still exists.
 *
 * Resolves within ~2 seconds whatever happens, and never rejects, so it cannot block logout.
 */
export const unregisterTwinrPushToken = () => {
  registeredUserId = null;
  if (!isTwinrApp()) {
    return Promise.resolve();
  }
  const unregister = async () => {
    const { token } = await getTwinrPushToken();
    if (token) {
      await postPushUnregister({ token });
    }
  };
  return withTimeout(unregister(), UNREGISTER_TIMEOUT_MS, undefined);
};
